import type { CorrectionResponse } from "@api/types";
import { useUser } from "@context/UserContext";
import { useCorrectionResult } from "@corrections/correctionsQueries";
import { describeSlip, type CorrectionSlip } from "@corrections/slip";
import {
  BrianBubble,
  BrianSlip,
  Button,
} from "@scribenote/brian-design-system";

const statusCopy = (r: CorrectionResponse): string | null => {
  switch (r.status) {
    case "pending":
      return "Still working on it…";
    case "silent":
    case "deferred":
    case "dropped":
      return `Brian could not finish that. Nothing was reported done.${
        r.effects_committed ? " Part of it may already have been done." : ""
      }`;
    case "refused":
      return "Your access changed. Sign in again and retry.";
    default:
      return null;
  }
};

export const CorrectionBubble = ({
  initial,
  text,
  sentAt,
  onDismiss,
}: {
  initial: CorrectionResponse;
  text: string;
  sentAt: Date;
  onDismiss: () => void;
}) => {
  const { currentUser } = useUser();
  const { data } = useCorrectionResult(initial.message_uuid, initial);
  const body = data.reply || statusCopy(data);

  return (
    <BrianBubble
      heard={{
        speaker: currentUser?.displayName ?? "You",
        time: sentAt,
        quote: text,
      }}
      variant={data.questions.length ? "asking" : "answer"}
      actions={
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {body && <p>{body}</p>}
        {data.slips.map((slip, i) => (
          <BrianSlip
            key={i}
            {...describeSlip(slip as unknown as CorrectionSlip)}
          />
        ))}
        {data.questions.map((q) => (
          <div key={q.uuid} className="text-base">
            <p className="text-mono-caps text-xs text-annotation">
              Brian asks:
            </p>
            <p>{q.prompt}</p>
            <p className="text-sm text-annotation">
              Answer it under Questions from Brian
            </p>
          </div>
        ))}
      </div>
    </BrianBubble>
  );
};
